/**
 * Sentry Configuration
 * Error tracking and performance monitoring for the frontend
 */

import * as Sentry from '@sentry/react';

const SENTRY_DSN = process.env.REACT_APP_SENTRY_DSN || '';
const ENVIRONMENT = process.env.REACT_APP_ENVIRONMENT || process.env.NODE_ENV || 'development';

export const initSentry = (): void => {
  if (!SENTRY_DSN) {
    console.log('Sentry DSN not configured, error tracking disabled');
    return;
  }

  Sentry.init({
    dsn: SENTRY_DSN,
    environment: ENVIRONMENT,  // Uses REACT_APP_ENVIRONMENT to distinguish staging vs production
    release: `congreso-transparente-frontend@${process.env.REACT_APP_VERSION || '0.1.0'}`,

    // Performance Monitoring - 100% sampling for full visibility into slow pages
    tracesSampleRate: parseFloat(process.env.REACT_APP_SENTRY_TRACES_SAMPLE_RATE || '1.0'),

    // Trace propagation targets (moved out of browserTracingIntegration for SDK v8+)
    tracePropagationTargets: [
      'localhost',
      /^https:\/\/congreso-transparente-back-end\.onrender\.com/,
      /^https:\/\/.*\.vercel\.app/,
    ],

    // Session Replay (optional, captures user sessions for debugging)
    replaysSessionSampleRate: 0.1, // 10% of sessions
    replaysOnErrorSampleRate: 1.0, // 100% of sessions with errors

    // Integrations
    integrations: [
      // Automatic browser tracing
      Sentry.browserTracingIntegration(),
      // Capture React component errors with component stack traces
      Sentry.replayIntegration({
        maskAllText: false,
        blockAllMedia: false,
      }),
    ],

    // Filter out noisy errors
    ignoreErrors: [
      // Common browser extensions errors
      'ResizeObserver loop limit exceeded',
      'ResizeObserver loop completed with undelivered notifications',
      // Network errors that are expected
      'Network request failed',
      'Failed to fetch',
      // User-initiated navigation
      'AbortError',
    ],

    // Don't send PII
    sendDefaultPii: false,

    // Before sending, you can modify or filter events
    beforeSend(event, hint) {
      // Don't send errors in development unless explicitly enabled
      if (process.env.NODE_ENV === 'development' && !process.env.REACT_APP_SENTRY_DEBUG) {
        console.log('Sentry event (dev mode, not sent):', event);
        return null;
      }
      return event;
    },
  });

  console.log('Sentry initialized for error tracking and performance monitoring');
};

// Export Sentry for use in components
export { Sentry };

// Helper to capture custom errors with context
export const captureError = (error: Error, context?: Record<string, unknown>): void => {
  Sentry.captureException(error, {
    extra: context,
  });
};

// Helper to add breadcrumbs for debugging
export const addBreadcrumb = (
  message: string,
  category: string,
  data?: Record<string, unknown>
): void => {
  Sentry.addBreadcrumb({
    message,
    category,
    data,
    level: 'info',
  });
};

// Set user context (call when user logs in, if applicable)
export const setUserContext = (userId: string, email?: string): void => {
  Sentry.setUser({
    id: userId,
    email,
  });
};

// Clear user context (call on logout)
export const clearUserContext = (): void => {
  Sentry.setUser(null);
};
